import { debounce } from "./utilities.js";
import { tooltipMouseEvents, tooltipPositionClasses } from "./tooltipVariables.js";
import { tooltipBuilder } from "./tooltipBuilder.js";
import { tooltipHoverEvents, tooltipClickEvents, tooltipScrollEvents } from "./tooltipEvents.js"


function tooltipJS() {
    // Store all cleanup functions for click tooltips
    const cleanupFunctions = new WeakMap();

    const body = document.body;
    const mouseEvent = tooltipMouseEvents();
    const tooltipClasses = tooltipPositionClasses();

    // Remove any existing tooltips first
    const existingTooltips = document.querySelectorAll('[data-tooltipContent]');
    existingTooltips.forEach(tooltip => tooltip.remove());

    const triggerOnHover = Array.from(document.querySelectorAll('[data-tooltip="hover"]:not([data-tooltip-bound="true"])'));
    const triggerOnClick = Array.from(document.querySelectorAll('[data-tooltip="click"]:not([data-tooltip-bound="true"])'));

    //=====================================================================
    // Add Trigger attributes and build All Tooltips + Add the tooltips to empty arrays
    //=====================================================================
    const buildTooltip = (trigger, indexOfTooltip) => {
        const tooltipInnerHtml = trigger.getAttribute("data-title");
        const tooltip = tooltipBuilder(trigger, indexOfTooltip, tooltipInnerHtml);
        const additionalClass = trigger.getAttribute('data-tooltipClassVariable');
        if (additionalClass) tooltip.classList.add(additionalClass);
        body.append(tooltip);
        return tooltip;
    };

    //=====================================================================
    // Hover Toolip
    //=====================================================================
    triggerOnHover.forEach((trigger, index) => {
        let tooltip; // Declare tooltip variable to maintain reference across events

        const showTooltip = () => {
            tooltip = buildTooltip(trigger, index); // Build the tooltip and store the reference
            tooltipHoverEvents(trigger, true, tooltipClasses, tooltip);
            trigger.removeEventListener(mouseEvent.hover, showTooltip); // Remove hover-in listener
            trigger.addEventListener(mouseEvent.hoverOut, hideTooltip); // Add hover-out listener
        };

        const hideTooltip = () => {
            if (tooltip) {
                tooltipHoverEvents(trigger, false, tooltipClasses, tooltip);
                trigger.addEventListener(mouseEvent.hover, showTooltip); // Re-add hover-in listener
                trigger.removeEventListener(mouseEvent.hoverOut, hideTooltip); // Remove hover-out listener
            }
        };

        trigger.addEventListener('mouseover', showTooltip);
        // Mark the trigger as "bound" to avoid duplicate listeners
        trigger.setAttribute('data-tooltip-bound', 'true');

    });


    //=====================================================================
    // Click Toolip
    //=====================================================================
    triggerOnClick.forEach((trigger, index) => {

        let tooltipIsActive;

        const windowEvents = (event) => {
            const tooltip = document.querySelector('[data-tooltipContent]');
            const currentButton = triggerOnClick[index];

            if (!tooltip.contains(event.target) && tooltipIsActive) {
                tooltipIsActive = false;
                tooltip.remove();
                currentButton.addEventListener('click', showTooltip);
                window.removeEventListener('click', windowEvents);
                window.removeEventListener('scroll', scrollDebounce);
            } else {
                tooltipIsActive = true;
            }
        }

        const showTooltip = () => {
            const tooltip = buildTooltip(trigger, index);
            window.addEventListener('click', windowEvents);
            tooltipClickEvents(triggerOnClick[index], tooltipClasses, tooltip);
            triggerOnClick[index].removeEventListener('click', showTooltip);
            window.addEventListener('scroll', scrollDebounce);
        }

        const scrollEvents = () => {
            const tooltip = document.querySelector('[data-tooltipContent]');
            tooltipScrollEvents(triggerOnClick[index], tooltip, tooltipClasses)
        }

        const scrollDebounce = debounce(scrollEvents, 200); 

        // Store cleanup function for this specific trigger
        cleanupFunctions.set(trigger, () => {
            window.removeEventListener('click', windowEvents);
            window.removeEventListener('scroll', scrollDebounce);
            trigger.removeEventListener('click', showTooltip);
        });

        trigger.addEventListener('click', showTooltip);
        // Mark the trigger as "bound" to avoid duplicate listeners
        trigger.setAttribute('data-tooltip-bound', 'true');
    });
}

// Add cleanup as a static method
tooltipJS.cleanup = (container = document.body) => {
    // Clean up any existing tooltips in the DOM
    const existingTooltips = document.querySelectorAll('[data-tooltipContent]');
    existingTooltips.forEach(tooltip => tooltip.remove());

    // Clean up click triggers in this container
    const clickTriggers = container.querySelectorAll('[data-tooltip="click"][data-tooltip-bound="true"]');
    clickTriggers.forEach(trigger => {
        const cleanup = cleanupFunctions.get(trigger);
        if (cleanup) cleanup();
        trigger.removeAttribute('data-tooltip-bound');
    });

    // Clean up hover triggers in this container
    const hoverTriggers = container.querySelectorAll('[data-tooltip="hover"][data-tooltip-bound="true"]');
    hoverTriggers.forEach(trigger => {
        trigger.removeAttribute('data-tooltip-bound');
    });
};

let isListenerAttached = false;

function initializeTooltips() {
    if (!isListenerAttached) {
        document.addEventListener('dynamicContentLoaded', () => {
            // Remove existing bound attributes before reinitializing
            document.querySelectorAll('[data-tooltip-bound="true"]').forEach(el => {
                el.removeAttribute('data-tooltip-bound');
            });
            tooltipJS();
        });
        isListenerAttached = true;
    }

    // Initial run
    tooltipJS();
}

// Add the cleanup method to the exported function
initializeTooltips.cleanup = tooltipJS.cleanup;

// Export the function for potential use in other files
export { initializeTooltips };